import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { determineEntitledDetailsForFocusAreaPage } from 'libs/end-to-end/src/focus-area-utils';
import { EntitledDetailsForFocusAreaPage } from 'libs/end-to-end/src/focus-area-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { SectionName } from 'libs/end-to-end/src/focus-area';
import { FocusAreaGroupTrendsSection } from 'apps/sigma2-ui/src/focus-area/FocusAreaGroupTrendsSection';
import { FocusAreaSolutionsSection } from 'apps/sigma2-ui/src/focus-area/FocusAreaSolutionsSection';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { FocusAreaPlaceholder } from 'apps/sigma2-ui/src/components/focus-area/Placeholder';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { isEmptyFocusArea } from 'apps/sigma2-ui/src/lib/focusArea';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { FocusAreaSummarySection } from 'apps/sigma2-ui/src/focus-area/FocusAreaSummarySection';
import { InsightsSection } from 'apps/sigma2-ui/src/pages/InsightsSection';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { FeatureFlags } from 'libs/end-to-end/src/feature-flag';
import { FocusAreaNewDataIntelligenceSummaryView } from 'apps/sigma2-ui/src/focus-area/FocusAreaNewDataIntelligenceSummaryView';
import { getFocusAreaDefinitionForMyObservation } from 'libs/end-to-end/src/my-observation-utils';
import { topAchievementInsights } from 'apps/sigma2-ui/src/models/user';
import { topRiskInsights } from 'apps/sigma2-ui/src/models/user';
import { topTreatments } from 'apps/sigma2-ui/src/models/user';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { InsightWithFocusArea } from 'libs/end-to-end/src/insights';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

interface FocusAreaPageProps {
  focusArea: FocusArea;
}

const EmptyState: React.FC<{
  disabled: boolean;
  focusArea: MyFocusAreaDefinition;
  focusAreaName: string;
}> = ({ focusArea, disabled }) => {
  return (
    <div>
      <div
        className="relative"
        style={{ overflow: 'hidden', maxHeight: 'calc(100vh - 233px)' }}
      >
        <div className="absolute left-0 bottom-0 w-full h-[80px] bg-gradient-to-t from-[#f7f7f7] to-transparent" />
      </div>

      <div
        className="absolute left-0 top-0 translate-x-[-40px] translate-y-[-190px]"
        style={{
          backdropFilter: 'blur(6px)',
          width: 'calc(100% + 80px)',
          height: 'calc(100% + 150px)',
        }}
      />

      <div className="absolute left-0 top-[400px] w-full h-full">
        <div className="flex h-full flex-col justify-center items-center">
          <FocusAreaPlaceholder
            focusArea={focusArea}
            status={disabled ? 'disabled' : 'empty'}
          />
        </div>
      </div>
    </div>
  );
};

export const SectionGradient: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <div
      style={{
        backgroundImage:
          'linear-gradient(180deg, rgba(31.22, 0, 61.62, 0.03) 0%, rgba(38.22, 0, 75.44, 0) 100%)',
      }}
    >
      {children}
    </div>
  );
};

export const FocusAreaPage = (props: FocusAreaPageProps) => {
  const { focusArea } = props;
  const { myUser, featureFlags } = useUser();
  const language = useLanguage();

  const { myObservation, selectedOrganizationId } = useObservation();

  const navigate = useNavigateSafe();

  const entitledDetailsForFocusAreaPage = useMemo(() => {
    return determineEntitledDetailsForFocusAreaPage({
      observation: myObservation,
      focusArea,
    });
  }, [focusArea, myObservation]);

  const { sectionName: sectionNameRaw } = useParams<{
    sectionName: string;
  }>();

  const sectionName = useMemo(() => {
    const parseResult = SectionName.safeParse(sectionNameRaw);

    const defaultSection = myObservation?.showNewDataIntelligenceDeepDivePage
      ? 'summary-v2'
      : 'summary';

    let parsedSectionName: SectionName = parseResult.success
      ? parseResult.data
      : defaultSection;

    if (
      myObservation &&
      parsedSectionName === 'solutions' &&
      !entitledDetailsForFocusAreaPage.areSolutionsEnabled
    ) {
      parsedSectionName = defaultSection;
    }

    return parsedSectionName;
  }, [
    myObservation,
    sectionNameRaw,
    entitledDetailsForFocusAreaPage.areSolutionsEnabled,
  ]);

  useEffect(() => {
    if (myObservation?.focusAreas?.every((fa) => fa.focusArea !== focusArea)) {
      navigate('/overview');
    }
  }, [myUser, focusArea, navigate, myObservation]);

  useAuthenticatedPageRedirect();

  const focusAreaDefinition = useMemo(() => {
    return getFocusAreaDefinitionForMyObservation({ myObservation, focusArea });
  }, [myObservation, focusArea]);

  const disabled = useMemo(() => {
    return (
      myObservation?.focusAreas?.every((fa) => fa.focusArea !== focusArea) ===
      true
    );
  }, [myObservation, focusArea]);

  const isEmptyState = isEmptyFocusArea({
    myObservation: myObservation,
    focusArea,
  });

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  // This used for the legacy summary section.
  const [currentDisplayedMetric, setCurrentDisplayedMetric] = useState<
    string | null
  >(null);

  const allFocusAreaDefinitions = useMemo(() => {
    return myObservation?.focusAreas || [];
  }, [myObservation]);

  const topTreatment = useMemo(() => {
    if (
      !myObservation ||
      myObservation.hideFocusAreaSolutionsPage ||
      !focusAreaDefinition
    ) {
      return null;
    }

    return (
      topTreatments({
        myObservation,
        focusArea: focusAreaDefinition.focusArea,
        metric: currentDisplayedMetric ?? undefined,
      }).at(0) ?? null
    );
  }, [currentDisplayedMetric, focusAreaDefinition, myObservation]);

  const keyTakeawayAchievements = useMemo(() => {
    if (!focusAreaDefinition) {
      return [];
    }

    const list = topAchievementInsights({
      insights: myObservation?.insights || null,
      focusArea: focusAreaDefinition.focusArea,
      metric: currentDisplayedMetric ?? null,
      allFocusAreaDefinitions,
    });

    if (myObservation?.showAllInsights && list.length > 0) {
      return [list[0]];
    }

    return list;
  }, [
    allFocusAreaDefinitions,
    currentDisplayedMetric,
    focusAreaDefinition,
    myObservation?.insights,
    myObservation?.showAllInsights,
  ]);

  const keyTakeawayRisk = useMemo(() => {
    if (!focusAreaDefinition) {
      return [];
    }

    const list = topRiskInsights({
      insights: myObservation?.insights || null,
      focusArea: focusAreaDefinition.focusArea,
      metric: currentDisplayedMetric ?? null,
      allFocusAreaDefinitions,
    });

    if (myObservation?.showAllInsights && list.length > 0) {
      return [list[0]];
    }

    return list;
  }, [
    myObservation,
    focusAreaDefinition,
    currentDisplayedMetric,
    allFocusAreaDefinitions,
  ]);

  const hasNoSummaryTab = useMemo(
    () =>
      !entitledDetailsForFocusAreaPage.summaryV2TabEnabled &&
      !entitledDetailsForFocusAreaPage.summaryLegacyTabEnabled,
    [
      entitledDetailsForFocusAreaPage.summaryLegacyTabEnabled,
      entitledDetailsForFocusAreaPage.summaryV2TabEnabled,
    ],
  );

  const getSectionTabs = useCallback(() => {
    return [
      ...(entitledDetailsForFocusAreaPage.summaryV2TabEnabled
        ? [
            {
              tabValue: 'summary-v2',
              automationTag: 'summary-v2-tab',
            } as const,
          ]
        : []),
      ...(entitledDetailsForFocusAreaPage.summaryLegacyTabEnabled
        ? [
            {
              tabValue: 'summary',
              automationTag: 'summary-tab',
            } as const,
          ]
        : []),
      ...(entitledDetailsForFocusAreaPage.binScatterMultiSelectEnabled
        ? [
            {
              tabValue: 'group-trends',
              automationTag: 'group-trends-tab',
            } as const,
          ]
        : []),
      ...(entitledDetailsForFocusAreaPage.areSolutionsEnabled
        ? [
            {
              tabValue: 'solutions',
              automationTag: 'solutions-tab',
            } as const,
          ]
        : []),
    ].filter(Boolean);
  }, [
    entitledDetailsForFocusAreaPage.areSolutionsEnabled,
    entitledDetailsForFocusAreaPage.binScatterMultiSelectEnabled,
    entitledDetailsForFocusAreaPage.summaryLegacyTabEnabled,
    entitledDetailsForFocusAreaPage.summaryV2TabEnabled,
  ]);

  const firstSection = useMemo(() => {
    const tabs = getSectionTabs();
    if (!tabs.length) {
      return null;
    }
    return tabs[0].tabValue;
  }, [getSectionTabs]);

  useEffect(() => {
    if (
      myObservation &&
      hasNoSummaryTab &&
      (!sectionName ||
        sectionName === 'summary' ||
        sectionName === 'summary-v2') &&
      firstSection
    ) {
      navigate(`/${focusArea.toLowerCase()}/${firstSection}`);
    }
  }, [
    firstSection,
    focusArea,
    hasNoSummaryTab,
    myObservation,
    navigate,
    sectionName,
  ]);

  if (!focusAreaDefinition) {
    return <></>;
  }

  return (
    <MainLayout showBackToTop={!isEmptyState}>
      <Helmet>
        <title>{documentTitle(focusAreaDefinition.displayName)}</title>
      </Helmet>

      <ObservationLoadingAndFailureIndicators
        isLoadingStill={isLoadingStill}
        failedToLoadObservation={failedToLoadObservation}
      />

      {!isLoadingStill &&
        myObservation &&
        entitledDetailsForFocusAreaPage.pageEnabled && (
          <>
            <SectionGradient>
              <PageSection>
                <Spacing direction="vertical" size="md">
                  <div className="mt-0">
                    <S2Typography
                      variant="headline-3"
                      translatedContent={language.dangerousUserInput({
                        value: focusAreaDefinition.displayName,
                      })}
                    />

                    <TabsRadioGroup
                      currValue={sectionName as string}
                      values={getSectionTabs()}
                      onChange={(value: string) => {
                        navigate(`/${focusArea.toLowerCase()}/${value}#top`);
                      }}
                      minWidth="auto"
                      gap="md"
                      underlineBgClass="bg-secondary"
                      renderTab={(tab) => {
                        const value = tab.value as {
                          tabValue: string;
                          automationTag: string;
                        };

                        const safeTabValue = SectionName.safeParse(
                          value.tabValue,
                        );

                        if (!safeTabValue.success) {
                          return null;
                        }

                        return (
                          <S2Typography
                            variant="headline-6-alt"
                            translatedContent={language.focusAreaSectionTab({
                              tabValue: safeTabValue.data,
                            })}
                            dangerouslyOverrideClassNameFn={
                              value.tabValue === sectionName
                                ? replaceTextColor('text-secondary')
                                : undefined
                            }
                          />
                        );
                      }}
                    />
                  </div>
                </Spacing>
              </PageSection>

              {isEmptyState && (
                <PageSection>
                  <EmptyState
                    disabled={disabled}
                    focusAreaName={focusAreaDefinition.displayName}
                    focusArea={focusAreaDefinition}
                  />
                </PageSection>
              )}

              {!isEmptyState && (
                <FocusAreaViews
                  sectionName={sectionName}
                  focusArea={focusAreaDefinition}
                  observation={myObservation}
                  organizationId={selectedOrganizationId}
                  featureFlags={featureFlags}
                  entitledDetailsForFocusAreaPage={
                    entitledDetailsForFocusAreaPage
                  }
                  keyTakeawayAchievements={keyTakeawayAchievements}
                  keyTakeawayRisk={keyTakeawayRisk}
                  topTreatment={topTreatment}
                  currentDisplayedMetric={currentDisplayedMetric}
                  setCurrentDisplayedMetric={setCurrentDisplayedMetric}
                />
              )}
            </SectionGradient>
          </>
        )}
    </MainLayout>
  );
};

const FocusAreaViews = ({
  sectionName,
  focusArea,
  observation,
  organizationId,
  entitledDetailsForFocusAreaPage,
  keyTakeawayAchievements,
  keyTakeawayRisk,
  topTreatment,
  currentDisplayedMetric,
  setCurrentDisplayedMetric,
}: {
  sectionName: SectionName;
  focusArea: MyFocusAreaDefinition;
  observation: MyObservation | null;
  organizationId: string | null;
  featureFlags: FeatureFlags | null;
  entitledDetailsForFocusAreaPage: EntitledDetailsForFocusAreaPage;
  keyTakeawayAchievements: InsightWithFocusArea[];
  keyTakeawayRisk: InsightWithFocusArea[];
  topTreatment: Treatment | null;
  currentDisplayedMetric: string | null;
  setCurrentDisplayedMetric: (metric: string | null) => void;
}) => {
  return (
    <>
      {sectionName === 'group-trends' &&
        observation &&
        organizationId &&
        entitledDetailsForFocusAreaPage.binScatterMultiSelectEnabled && (
          <PageSection>
            <FocusAreaGroupTrendsSection
              focusArea={focusArea}
              myObservation={observation}
              organizationId={organizationId}
            />
          </PageSection>
        )}
      {sectionName === 'solutions' &&
        entitledDetailsForFocusAreaPage.areSolutionsEnabled && (
          <PageSection>
            <FocusAreaSolutionsSection focusArea={focusArea} />
          </PageSection>
        )}
      {sectionName === 'summary' && observation && organizationId && (
        <PageSection>
          <FocusAreaSummarySection
            focusAreaDefinition={focusArea}
            observation={observation}
            keyTakeawayAchievements={keyTakeawayAchievements}
            keyTakeawayRisk={keyTakeawayRisk}
            topTreatment={topTreatment}
            entitledDetailsForFocusAreaPage={entitledDetailsForFocusAreaPage}
            currentDisplayedMetric={currentDisplayedMetric}
            setCurrentDisplayedMetric={setCurrentDisplayedMetric}
          />
        </PageSection>
      )}
      {sectionName === 'summary-v2' &&
        observation &&
        organizationId &&
        observation.showNewDataIntelligenceDeepDivePage &&
        entitledDetailsForFocusAreaPage.entitledToBiasAnalysis && (
          <PageSection>
            <FocusAreaNewDataIntelligenceSummaryView
              focusAreaDefinition={focusArea}
              observation={observation}
              organizationId={organizationId}
              entitledDetailsForFocusAreaPage={entitledDetailsForFocusAreaPage}
            />
          </PageSection>
        )}
      {sectionName === 'insights' &&
        entitledDetailsForFocusAreaPage.entitledToBiasAnalysis && (
          <PageSection>
            <InsightsSection focusArea={focusArea} />
          </PageSection>
        )}
    </>
  );
};
